import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { createLogisticsApi, DeliveryOrderPatch } from '../apis/logistics.js';
import type { ILogisticsRevisionHandle } from '../apis/logisticsRevision.js';
import { DELIVERED_STATUS_ID, type Delivery, type SiteLogistics } from '../interfaces/ILogistics.js';
import { buildMergeSnapshotBody, type IMergeSnapshotInput } from '../logistics/mergeSnapshot.js';
import { logisticsKeys } from './logisticsKeys.js';
import { OWNS_ERROR_FEEDBACK } from './mutationFeedback.js';

type ILogisticsApi = ReturnType<typeof createLogisticsApi>;

/**
 * The payload as it will read once a card has moved. The server stamps
 * `deliveredAt` on a move INTO Delivered and clears it on a move out, so the
 * optimistic copy mirrors both — a card keyed on `deliveredAt` alone would
 * otherwise wear the wrong summary until the refetch lands. `now` is
 * approximate on purpose (the refetch replaces it); `?? now` keeps a re-drop
 * within Delivered from shifting an arrival the server would leave alone.
 */
export function withCardMoved(logistics: SiteLogistics, ref: string, status: string, now: string): SiteLogistics {
  return {
    ...logistics,
    deliveries: logistics.deliveries.map((delivery) =>
      delivery.ref === ref
        ? {
            ...delivery,
            status,
            deliveredAt: status === DELIVERED_STATUS_ID ? (delivery.deliveredAt ?? now) : null,
          }
        : delivery,
    ),
  };
}

export interface IPatchOrderVariables {
  ref: string;
  patch: DeliveryOrderPatch;
  merge?: IMergeSnapshotInput;
}

/**
 * Saving a delivery, then (optionally) folding it into the running stock.
 * Sequential, not parallel: the snapshot is only correct if the arrival was
 * actually recorded, so a failed PATCH must not leave stock claiming material
 * no delivery vouches for. Resolves to the server's order.
 */
export async function patchOrderWithMerge(
  api: Pick<ILogisticsApi, 'patchDeliveryOrder' | 'postInventorySnapshot'>,
  siteId: string,
  revision: ILogisticsRevisionHandle | null,
  { ref, patch, merge }: IPatchOrderVariables,
) {
  const order = await api.patchDeliveryOrder(siteId, ref, patch, revision);
  if (merge) await api.postInventorySnapshot(siteId, buildMergeSnapshotBody(merge), revision);
  return order;
}

/** The optimistic board writes (moves, stars): only the last one to settle refetches. */
const boardWriteKey = (siteId: string) => ['logistics-move-card', siteId];

export interface IUseMoveCardArgs<TVariables> {
  siteId: string;
  revision: ILogisticsRevisionHandle | null;
  patchDeliveryOrder: ILogisticsApi['patchDeliveryOrder'];
  /** Which card goes to which column — each app's mutation variables differ. */
  toMove: (variables: TVariables) => { ref: string; status: string };
  onSuccess?: (order: Awaited<ReturnType<ILogisticsApi['patchDeliveryOrder']>>, variables: TVariables) => void;
  /** Runs after the optimistic move was undone — and reports the failure:
   *  the mutation declares it owns its error feedback. */
  onError: (error: Error, variables: TVariables) => void;
}

/**
 * A card dropped on another column. Optimistic, unlike every other write: the
 * card must land where it was released, and waiting a round trip reads as a
 * failed drag. Several moves can be in flight at once, so each failure restores
 * the snapshot taken before ITS move and only the last move to settle refetches
 * — an earlier settle would otherwise pull the server's answer under a move
 * still on its way.
 */
export function useMoveCardMutation<TVariables>({
  siteId,
  revision,
  patchDeliveryOrder,
  toMove,
  onSuccess,
  onError,
}: IUseMoveCardArgs<TVariables>) {
  const queryClient = useQueryClient();
  const queryKey = logisticsKeys.page(siteId);
  const mutationKey = boardWriteKey(siteId);
  return useMutation({
    mutationKey,
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (variables: TVariables) => {
      const { ref, status } = toMove(variables);
      return patchDeliveryOrder(siteId, ref, { status }, revision);
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SiteLogistics>(queryKey);
      if (previous) {
        const { ref, status } = toMove(variables);
        queryClient.setQueryData<SiteLogistics>(queryKey, withCardMoved(previous, ref, status, new Date().toISOString()));
      }
      return { previous };
    },
    onSuccess: (order, variables) => onSuccess?.(order, variables),
    onError: (error, variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      onError(error, variables);
    },
    onSettled: () => {
      // This move is still counted while its own settle runs: 1 means last.
      if (queryClient.isMutating({ mutationKey }) === 1) void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export interface IUseToggleCriticalArgs {
  siteId: string;
  revision: ILogisticsRevisionHandle | null;
  patchDeliveryOrder: ILogisticsApi['patchDeliveryOrder'];
  /** Runs after the optimistic star was undone — and reports the failure:
   *  the mutation declares it owns its error feedback. */
  onError: (error: Error) => void;
}

/** That order with its star set; every other order untouched. */
function withStar(logistics: SiteLogistics, ref: string, isCritical: boolean): SiteLogistics {
  return {
    ...logistics,
    deliveries: logistics.deliveries.map((order) => (order.ref === ref ? { ...order, isCritical } : order)),
  };
}

/** Stars or unstars an order. Optimistic, like a card move: the star fills and
 *  the card takes its new place at the tap. A refused write undoes this star
 *  alone, never a move or another star made since. Shares the moves' key, so
 *  neither refetches over the other's optimistic write. */
export function useToggleCriticalMutation({ siteId, revision, patchDeliveryOrder, onError }: IUseToggleCriticalArgs) {
  const queryClient = useQueryClient();
  const queryKey = logisticsKeys.page(siteId);
  const mutationKey = boardWriteKey(siteId);
  const setStar = (ref: string, isCritical: boolean) =>
    queryClient.setQueryData<SiteLogistics>(queryKey, (current) => current && withStar(current, ref, isCritical));
  return useMutation({
    mutationKey,
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (delivery: Delivery) =>
      patchDeliveryOrder(siteId, delivery.ref, { isCritical: !delivery.isCritical }, revision),
    onMutate: async (delivery) => {
      await queryClient.cancelQueries({ queryKey });
      setStar(delivery.ref, !delivery.isCritical);
    },
    onError: (error, delivery) => {
      setStar(delivery.ref, delivery.isCritical);
      onError(error);
    },
    onSettled: () => {
      // This write is still counted while its own settle runs: 1 means last.
      if (queryClient.isMutating({ mutationKey }) === 1) void queryClient.invalidateQueries({ queryKey });
    },
  });
}
